import { NavigationProp, useNavigation } from '@react-navigation/native';
import { theme } from 'aimo-ui';
import DropDownButton from 'components/common/DropDownButton';
import VehicleAvatar from 'components/common/VehicleAvatar';
import Icon from 'components/icons/Icon';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermitStore } from 'stores/permitStore';
import styled from 'styled-components/native';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import { Vehicle } from 'types/generatedSchemaTypes';
import { isWeb } from 'utils/deviceUtils';
import { isParkZoneResume } from 'utils/navigationUtils';
import { Row } from '../styles/styles';
import { useUiStore } from 'stores/uiStore';

const VehicleSelectDropDownWrapper = styled.View`
  flex: 1 0 0;
  max-width: ${() => (isWeb ? '100%' : '30%')};
`;

type VehicleSelectDropDownProps = {
  isDisabled?: boolean;
  showInfoIconIfNotSelected?: boolean;
  allowMultipleSelection?: boolean;
  handleVehiclesSelect?: () => void;
  skipAutomaticSelection?: boolean;
  currentVehiclePlate?: string;
  setVehicleState?: (vehicle?: Vehicle) => void;
};

const VehicleSelectDropDown = ({
  isDisabled = false,
  showInfoIconIfNotSelected = false,
  allowMultipleSelection = false,
  handleVehiclesSelect,
  skipAutomaticSelection = false,
  setVehicleState,
  currentVehiclePlate,
}: VehicleSelectDropDownProps) => {
  const { t } = useTranslation();
  const vehicle = useSelectedParkingVehicle(
    skipAutomaticSelection,
    undefined,
    currentVehiclePlate
  );
  const { isLoggedIn } = useIsLoggedIn();
  const navigation: NavigationProp<any> = useNavigation();
  const isResume = isParkZoneResume(navigation);
  const selectedLicensePlates = usePermitStore(
    (state) => state.selectedLicensePlatesPermitRedeem
  );

  useEffect(() => {
    if (setVehicleState && vehicle) {
      setVehicleState(vehicle);
    }
  }, [setVehicleState, vehicle]);

  const handleOnPress = () => {
    if (allowMultipleSelection) {
      handleVehiclesSelect && handleVehiclesSelect();
    } else {
      useUiStore.getState().setSheetModal('parkingVehicle');
    }
  };

  const { name, licensePlate, appSettings } = vehicle || {};
  const displayName = name || licensePlate?.plateText;

  const text = useMemo(() => {
    if (allowMultipleSelection) {
      switch (selectedLicensePlates.length) {
        case 0:
          return t('parking.parkingProcess.selectVehicle');
        case 1:
          return selectedLicensePlates[0].plateText;
        default:
          return t('permit.access.vehicles', {
            count: selectedLicensePlates.length,
          });
      }
    } else {
      return displayName || t('parking.parkingProcess.noVehicleSelected');
    }
  }, [allowMultipleSelection, displayName, selectedLicensePlates, t]);

  const showInfoIcon = useMemo(() => {
    if (allowMultipleSelection) {
      return showInfoIconIfNotSelected && selectedLicensePlates.length === 0;
    } else {
      return showInfoIconIfNotSelected && !licensePlate;
    }
  }, [
    allowMultipleSelection,
    licensePlate,
    selectedLicensePlates.length,
    showInfoIconIfNotSelected,
  ]);

  return (
    <VehicleSelectDropDownWrapper>
      <DropDownButton
        testID="vehicle-select-dropdown"
        disabled={!isLoggedIn || isResume || isDisabled}
        onPress={handleOnPress}
        text={text}
        showInfoIcon={showInfoIcon}
        leftItem={
          <Row>
            {!allowMultipleSelection && displayName && (
              <VehicleAvatar
                color={appSettings?.color || theme.colors.boost.green}
                icon={appSettings?.icon || ' '}
              />
            )}
            {vehicle?.cameraRecognition?.enabled && (
              <ColumnWithMargins ml={5}>
                <Icon name="CCTV-Outlined" />
              </ColumnWithMargins>
            )}
          </Row>
        }
      />
    </VehicleSelectDropDownWrapper>
  );
};

export default VehicleSelectDropDown;
